import { Empty } from 'cpool-components/state/Empty';
import type { SchemaLoanRequestResolver_GetResubmissionLoanRequestsByDate } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { LuBell } from 'react-icons/lu';

import { ResubmissionItem } from './ResubmissionItem';
import { CountBadge } from '../CountBadge';
import { IconBadge } from '../IconBadge';
import Spinner from '~/components/icons/Spinner';
import { useResubmissions } from '~/features/dashboard/hooks/useResubmissions';
import { endOfTodayUtc, startOfTodayUtc } from '~/features/dashboard/utils/resubmissionDateRange';

export const Resubmissions: React.FC = () => {
    const { t } = useTranslation();
    const { data, isError, isLoading } = useResubmissions({ end: endOfTodayUtc(), start: startOfTodayUtc() });

    if (!isError && (isLoading || !data)) {
        return (
            <div className="w-full inset-0 flex items-center justify-center z-50">
                <Spinner />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="py-2.5 px-5">
                <span>{t('DASHBOARD.ERROR')}</span> <br />
                <span>{t('DASHBOARD.RELOAD')}</span>
            </div>
        );
    }

    data.sort(
        (
            a: SchemaLoanRequestResolver_GetResubmissionLoanRequestsByDate[number],
            b: SchemaLoanRequestResolver_GetResubmissionLoanRequestsByDate[number],
        ) => {
            if (!a.resubmission || !b.resubmission) {
                return 0;
            }
            return a.resubmission - b.resubmission;
        },
    );

    if (data.length === 0) {
        return (
            <div className="flex items-center justify-center h-full">
                <Empty
                    noBorder
                    icon={<LuBell />}
                    iconClassName="text-red-500"
                    iconContainerClassName="bg-red-500/10 rounded-lg"
                    title={t('DASHBOARD.RESUBMISSIONS.TITLE', { count: data.length })}
                    description={t('DASHBOARD.RESUBMISSIONS.NO_RESUBMISSIONS')}
                />
            </div>
        );
    }

    return (
        <section className="py-1">
            <div className="px-5 pb-3">
                <div className="flex items-center justify-between min-h-[28px]">
                    <h6 className="text-midnight flex items-center gap-4">
                        <IconBadge icon={<LuBell />} className="text-red-500 bg-red-500/10" />
                        <div className="flex flex-col">
                            <span className="text-lg font-medium leading-none mb-0.5">
                                {t('DASHBOARD.RESUBMISSIONS.TITLE', { count: data.length })}
                            </span>
                            <span className="text-sm">
                                {t('DASHBOARD.RESUBMISSIONS.SUBTITLE', { count: data.length })}
                            </span>
                        </div>
                    </h6>
                    <CountBadge count={data.length} className="text-red-500 bg-red-500/10" />
                </div>
            </div>
            <div className="overflow-y-auto max-h-[180px] scrollbar-thin scrollbar-thumb-rounded-md scrollbar-thumb-dark scrollbar-track-grey-stroke scrollbar-track-rounded-md">
                <div className="px-1 flex flex-col">
                    {data.map((loanRequest) => {
                        return <ResubmissionItem key={loanRequest.id} data={loanRequest} />;
                    })}
                </div>
            </div>
        </section>
    );
};
